import { FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Field, useFormikContext } from 'formik';

import AutocompleteByOptionId from '../AutocompleteByOptionId';
import { Context as PaymentsTerminalsContext } from '../../api/paymentsTerminals/context';
import { getDataOptions } from '../../utils/arrayUtils';

type Props = {
    /** Formik path. Every caller uses `paymentMethodObject.paymentsTerminal`; it
     *  stays a prop so a future form can put the terminal elsewhere. */
    name?: string;
    disabled?: boolean;
    /**
     * Restrict the offer to these terminal ids — the terminals a travel file was
     * actually charged on. Card money can only be given back through the terminal
     * it came in on, so a repay does not get a free choice.
     *
     * Undefined or empty means "not knowable" (every document issued before the
     * terminal was recorded), NOT "nothing allowed": the full list is offered
     * with a warning instead, because refusing there would block every legacy
     * repay.
     */
    restrictToIds?: number[];
    /** Extra caption under the field, when the caller has more to say. */
    helperText?: ReactNode;
    /** Caption shown when `restrictToIds` is empty — i.e. the terminal cannot be
     *  verified. Omit to say nothing. */
    unverifiedText?: ReactNode;
};

/**
 * The "Terminal Name" picker, shared by the Receipt, Remittance, Pre-Receipt and
 * Repay forms.
 *
 * Options come from the `paymentsterminals` table (Setup > Finance > Payments
 * Terminals) — never a hardcoded terminal. When the table has none, the field is
 * empty AND says so: the schema marks it required, so submit is blocked, and
 * `resolvePelecardTerminal` refuses to open a Pelecard session server-side.
 * Before this, a missing terminal silently charged through the Taytan test
 * terminal.
 */
const PaymentsTerminalField: FC<Props> = ({
    name = 'paymentMethodObject.paymentsTerminal',
    disabled,
    restrictToIds,
    helperText,
    unverifiedText
}) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const paymentsTerminalsContext = useContext(PaymentsTerminalsContext);

    const liveTerminals = useMemo(
        () =>
            getDataOptions(paymentsTerminalsContext.data || []).filter(
                (terminal: any) => !terminal.isDeleted && terminal.provider === 'PELECARD'
            ),
        [paymentsTerminalsContext.data]
    );

    useEffect(() => {
        if (!liveTerminals.length) paymentsTerminalsContext.get();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isRestricted = !!restrictToIds?.length;
    const options = useMemo(
        () =>
            isRestricted
                ? liveTerminals.filter((terminal: any) => restrictToIds!.includes(terminal._id))
                : liveTerminals,
        [liveTerminals, restrictToIds, isRestricted]
    );

    // Exactly one terminal is possible, so there is nothing to choose — select it.
    // Only when the field is still empty: an amend keeps the terminal it carries.
    const current = name.split('.').reduce((acc: any, key) => acc?.[key], values);
    useEffect(() => {
        if (current || !isRestricted || options.length !== 1) return;
        setFieldValue(name, options[0]);
    }, [current, isRestricted, options]); // eslint-disable-line react-hooks/exhaustive-deps

    const noneConfigured = !liveTerminals.length;
    const caption = noneConfigured
        ? 'No clearing terminal is configured. Add one in Setup > Finance > Payments Terminals — a card payment cannot be taken or repaid until then.'
        : isRestricted
          ? helperText
          : unverifiedText;

    return (
        <Box pb={1}>
            <Field
                component={AutocompleteByOptionId}
                label="Terminal Name"
                name={name}
                disabled={disabled || (isRestricted && options.length === 1)}
                options={options}
                onChange={setFieldValue}
                fullOption
                fullWidth
                required
            />
            {caption && (
                <Typography
                    variant="caption"
                    color={noneConfigured || !isRestricted ? 'warning.main' : 'text.secondary'}
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    {caption}
                </Typography>
            )}
        </Box>
    );
};

export default PaymentsTerminalField;
